import type { WallRun, Tower, Gate } from '../../../data/types';
import type { ReservedFootprint } from '../types';
import { bufferPolyline, inflate } from '../../../geo/clip';
import { towerPolygon } from './towers';
import { NO_WATER, type WaterTest } from './moat';
import { gateFootprint } from './gates';
import { citySide, wallSection } from './wallArcade';
import { offsetPolyline } from '../planGeometry';

/*
 * Towers are kept clear over the footprint they are built on, which for a half-round tower depends
 * on the water beside its wall (towers.ts). Without the water a half-round tower's reservation takes
 * the side of the wall it stands off; the real layout's lots come out the same either way (5334
 * houses, 77 back houses), as none reach the moat side.
 */
export function collectFortificationReserved(
  walls: readonly WallRun[],
  towers: readonly Tower[],
  gates: readonly Gate[],
  isWater: WaterTest = NO_WATER
): readonly ReservedFootprint[] {
  const reserved: ReservedFootprint[] = [];

  /* A wall's arcade is kept clear as the wall is, a metre beyond its front. */
  for (const wall of walls) {
    const depth = wallSection(wall.height)?.depth ?? 0;
    const side = citySide(wall.path, wall.thickness, isWater);
    const centre = offsetPolyline(wall.path, (side * depth) / 2);
    const buffered = bufferPolyline(centre, wall.thickness / 2 + depth / 2 + 1, 'butt');
    for (let i = 0; i < buffered.length; i++) {
      reserved.push({
        id: `${wall.id}:${i}`,
        polygon: buffered[i]!,
      });
    }
  }

  for (const tower of towers) {
    const poly = towerPolygon(tower, { walls, isWater });
    const inflated = inflate([{ outer: poly, holes: [] }], 1, 'round');
    for (const p of inflated) {
      reserved.push({
        id: tower.id,
        polygon: p,
      });
    }
  }

  for (const gate of gates) {
    const poly = gateFootprint(gate);
    const inflated = inflate([poly], 1, 'round');
    for (const p of inflated) {
      reserved.push({
        id: gate.id,
        polygon: p,
      });
    }
  }

  return reserved;
}
